import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import { toast } from 'react-toastify';
import { api } from '../services/AuthService';
import { useAuth } from '../services/AuthService';

function MotoboysManagement() {
  const [motoboys, setMotoboys] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [editingMotoboy, setEditingMotoboy] = useState(null);
  const [selectedMotoboy, setSelectedMotoboy] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    motoboy_scope: 'LOJA',
    lanchonete_id: '',
    vehicle_plate: '',
    vehicle_type: 'moto',
    available: true
  });
  const { isAdminMaster, isAdminLanchonete, getLanchoneteId } = useAuth();

  const loadMotoboys = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/motoboys');
      setMotoboys(response.data);
    } catch (error) {
      toast.error('Erro ao carregar motoboys');
      console.error('Erro ao carregar motoboys:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMotoboys();
  }, [loadMotoboys]);

  const loadDeliveries = async (motoboyId, date) => {
    try {
      setReportLoading(true);
      const response = await api.get(`/motoboys/${motoboyId}/deliveries-by-date?date=${date}`);
      setDeliveries(response.data);
    } catch (error) {
      toast.error('Erro ao carregar entregas');
    } finally {
      setReportLoading(false);
    }
  };

  const handleOpenReport = (motoboy) => {
    setSelectedMotoboy(motoboy);
    setReportDialogOpen(true);
    loadDeliveries(motoboy.id, reportDate);
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setReportDate(newDate);
    if (selectedMotoboy) {
      loadDeliveries(selectedMotoboy.id, newDate);
    }
  };

  const handleOpenDialog = (motoboy = null) => {
    if (motoboy) {
      setEditingMotoboy(motoboy);
      setFormData({
        name: motoboy.name,
        email: motoboy.email,
        password: '',
        phone: motoboy.phone,
        motoboy_scope: motoboy.motoboy_scope || 'LOJA',
        lanchonete_id: motoboy.lanchonete_id || '',
        vehicle_plate: motoboy.vehicle_plate,
        vehicle_type: motoboy.vehicle_type,
        available: motoboy.available
      });
    } else {
      setEditingMotoboy(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        phone: '',
        motoboy_scope: 'LOJA',
        lanchonete_id: '',
        vehicle_plate: '',
        vehicle_type: 'moto',
        available: true
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingMotoboy(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      if (editingMotoboy) {
        if (!submitData.password) delete submitData.password;
        await api.put(`/users/${editingMotoboy.id}`, submitData);
        await api.put(`/motoboys/${editingMotoboy.id}`, {
          vehicle_plate: submitData.vehicle_plate,
          vehicle_type: submitData.vehicle_type,
          available: submitData.available
        });
        toast.success('Motoboy atualizado com sucesso!');
      } else {
        if (!submitData.password) {
          toast.error('Senha é obrigatória para novos motoboys');
          return;
        }
        await api.post('/users/motoboy', {
          name: submitData.name,
          email: submitData.email,
          password: submitData.password,
          phone: submitData.phone,
          motoboy_scope: submitData.motoboy_scope,
          lanchonete_id: submitData.motoboy_scope === 'APP' ? null : (isAdminLanchonete() ? getLanchoneteId() : submitData.lanchonete_id || null)
        });
        toast.success('Motoboy criado com sucesso!');
      }
      handleCloseDialog();
      loadMotoboys();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar motoboy');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este motoboy?')) {
      try {
        await api.delete(`/users/${id}`);
        toast.success('Motoboy excluído com sucesso!');
        loadMotoboys();
      } catch (error) {
        toast.error('Erro ao excluir motoboy');
      }
    }
  };

  const handleToggleStatus = async (id, available) => {
    try {
      await api.patch(`/users/${id}/status`, { available });
      toast.success(`Motoboy ${available ? 'ativado' : 'desativado'} com sucesso!`);
      loadMotoboys();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          {isAdminLanchonete() ? 'Motoboys da Minha Lanchonete' : 'Todos os Motoboys'}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>Novo Motoboy</Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Contato</TableCell>
              <TableCell>Escopo</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {motoboys.map((motoboy) => (
              <TableRow key={motoboy.id}>
                <TableCell>
                  <Typography variant="subtitle1" fontWeight="bold">{motoboy.name}</Typography>
                  <Typography variant="body2" color="textSecondary">{motoboy.email}</Typography>
                </TableCell>
                <TableCell>{motoboy.phone}</TableCell>
                <TableCell>
                  <Chip 
                    label={motoboy.motoboy_scope === 'APP' ? 'Aplicativo' : 'Loja'} 
                    color={motoboy.motoboy_scope === 'APP' ? 'primary' : 'secondary'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Switch
                    checked={Boolean(motoboy.available)}
                    onChange={() => handleToggleStatus(motoboy.id, !motoboy.available)}
                    color="primary"
                  />
                </TableCell>
                <TableCell align="right">
                  {isAdminMaster() && (
                    <IconButton color="info" onClick={() => handleOpenReport(motoboy)} title="Relatório de Entregas">
                      <HistoryIcon />
                    </IconButton>
                  )}
                  <IconButton color="primary" onClick={() => handleOpenDialog(motoboy)} title="Editar"><EditIcon /></IconButton>
                  <IconButton color="error" onClick={() => handleDelete(motoboy.id)} title="Excluir"><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog Novo/Editar */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingMotoboy ? 'Editar Motoboy' : 'Novo Motoboy'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Nome" name="name" value={formData.name} onChange={handleInputChange} margin="normal" required />
          <TextField fullWidth label="Email" name="email" value={formData.email} onChange={handleInputChange} margin="normal" required />
          <TextField fullWidth label="Senha" name="password" type="password" value={formData.password} onChange={handleInputChange} margin="normal" required={!editingMotoboy} />
          <TextField fullWidth label="Telefone" name="phone" value={formData.phone} onChange={handleInputChange} margin="normal" required />
          <FormControl fullWidth margin="normal">
            <InputLabel>Escopo</InputLabel>
            <Select name="motoboy_scope" value={formData.motoboy_scope} onChange={handleInputChange} label="Escopo">
              <MenuItem value="LOJA">LOJA (entrega própria)</MenuItem>
              <MenuItem value="APP">APP (motoboy do aplicativo)</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">{editingMotoboy ? 'Atualizar' : 'Criar'}</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Relatório */}
      <Dialog open={reportDialogOpen} onClose={() => setReportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Relatório de Entregas - {selectedMotoboy?.name}</DialogTitle>
        <DialogContent>
          <Box display="flex" alignItems="center" gap={2} mb={3} mt={1}>
            <TextField
              type="date"
              label="Data do Relatório"
              value={reportDate}
              onChange={handleDateChange}
              InputLabelProps={{ shrink: true }}
            />
            <Typography variant="h6" color="primary">
              Total: {deliveries.length} entregas
            </Typography>
          </Box>
          
          {reportLoading ? (
            <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
          ) : (
            <List>
              {deliveries.length === 0 ? (
                <Typography color="textSecondary" align="center">Nenhuma entrega encontrada nesta data.</Typography>
              ) : (
                deliveries.map((delivery) => (
                  <React.Fragment key={delivery.id}>
                    <ListItem>
                      <ListItemText 
                        primary={`Pedido #${delivery.id} - ${delivery.lanchonete_name}`}
                        secondary={`Cliente: ${delivery.customer_name} | Valor: R$ ${Number(delivery.total_amount).toFixed(2)} | Hora: ${new Date(delivery.created_at).toLocaleTimeString()}`}
                      />
                      <Chip label="Entregue" color="success" size="small" />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MotoboysManagement;
